import { useMemo } from 'react';
import { Tooltip } from '../../../../components/popover/tooltip';
import { defineColumn, type StrictColumnDef } from '../../../../components/tanstack-table/column';
import type { OrderOptions } from '../../../../data/order-options';
import { compare } from '../../../../helpers/column-sort';
import { itemHasUserCount, type ProductOrderItem } from '../data/product-order-item';
import { antiRolloff, minQOH, stockBelowOneItem, underThreshold } from '../utils';

export function useReorder(options: OrderOptions): StrictColumnDef<ProductOrderItem> {
  // Depend on primitives, not the options object reference, so reorderColumn
  // stays stable across refreshes that ship a new options object with the same
  // values — avoids cascading column-def recomputation and the TanStack cell
  // remounts that follow. Hygiene rather than a bug fix today: cells are
  // stateless, so a remount has no visible effect.
  const { qoh_threshold_weeks, reorder_weeks } = options;

  const reorderLabel = useMemo(() => {
    return (orderItem: ProductOrderItem) => {
      if (orderItem.product.disabled) {
        return 'DISABLED';
      }

      if (orderItem.product.auto_disabled) {
        return 'AUTO_DSBL';
      }

      if (reorder_weeks === 0) {
        return 'NO';
      }

      if (
        underThreshold(orderItem.product, qoh_threshold_weeks) ||
        stockBelowOneItem(orderItem.product)
      ) {
        return 'YES';
      }

      if (minQOH(orderItem.product)) {
        return 'MIN_QOH';
      }

      if (antiRolloff(orderItem.product)) {
        return 'ROLLOFF';
      }

      return 'NO';
    };
  }, [qoh_threshold_weeks, reorder_weeks]);

  return useMemo(() => {
    const labelToSortOrder: Record<string, number> = {
      DISABLED: -1,
      AUTO_DSBL: -1,
      YES: 2,
      MIN_QOH: 2,
      ROLLOFF: 1,
      NO: 0,
    };

    return defineColumn<ProductOrderItem>({
      id: 'Reorder',
      minWidth: 70,
      accessorFn: (orderItem) => {
        if (itemHasUserCount(orderItem)) {
          return 'USER';
        }

        return reorderLabel(orderItem);
      },
      cell: ({ item: orderItem, value: label }) => {
        if (label === 'DISABLED') {
          return (
            <Tooltip content="You disabled this product. It won't appear in reorder suggestions.">
              <div className="text-center has-tooltip">{label}</div>
            </Tooltip>
          );
        }

        if (label === 'AUTO_DSBL') {
          return (
            <Tooltip content="Orderbot stopped suggesting this for reorder because it hasn't been selling. Suggestions resume automatically once sales pick up.">
              <div className="text-center has-tooltip">{label}</div>
            </Tooltip>
          );
        }

        if (label === 'MIN_QOH' && orderItem.product.min_qoh) {
          const min_qoh = orderItem.product.min_qoh;
          const case_qty = orderItem.product.case_qty;
          const threshold =
            case_qty && case_qty > 0 && min_qoh > case_qty
              ? `${min_qoh / case_qty} case(s)`
              : `${min_qoh} unit(s)`;

          return (
            <Tooltip
              content={`Stock is below your Min QOH of ${threshold}. Orderbot is suggesting a reorder.`}
            >
              <div className="text-center has-tooltip">{label}</div>
            </Tooltip>
          );
        }

        if (label === 'ROLLOFF') {
          return (
            <Tooltip content="You're out of stock but this has sold recently. Orderbot is suggesting one unit so it stays on the shelf.">
              <div className="text-center has-tooltip">{label}</div>
            </Tooltip>
          );
        }

        return (
          <div className={`text-center ${label === 'USER' ? 'font-extrabold' : ''}`}>{label}</div>
        );
      },
      sortingFn: (rowA, rowB) => {
        const aOrder = labelToSortOrder[reorderLabel(rowA)];
        const bOrder = labelToSortOrder[reorderLabel(rowB)];

        return compare(aOrder, bOrder);
      },
      gloss: 'Whether Orderbot is suggesting this for reorder. If not, hover the cell to see why.',
    });
  }, [reorderLabel]);
}
